import { motion } from 'framer-motion';
import { ArrowRight, Zap, Wrench, Sun, Building2, Users, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { projects } from '../data/projects';
import { fadeUp, staggerContainer, defaultViewport } from '../components/ui/AnimationVariants';

const stats = [
  { icon: Building2, value: '500+',  label: 'Projects Delivered' },
  { icon: Users,     value: '50+',   label: 'Field & Office Staff' },
  { icon: Award,     value: '15+',   label: 'Years in the Field' },
  { icon: Zap,       value: '$100M+',label: 'Total Project Volume' },
];

const services = [
  {
    icon: Zap,
    title: 'Commercial Electrical',
    description:
      'Design-build and bid-build electrical for commercial, mixed-use, and large residential projects. We work off engineered drawings or coordinate directly with the design team during preconstruction.',
  },
  {
    icon: Wrench,
    title: 'Self-Performed Installation',
    description:
      'We do not subcontract our core electrical work. Our own foremen and licensed electricians execute rough-in, gear installation, and trim-out — giving GCs a single accountable point of contact in the field.',
  },
  {
    icon: Sun,
    title: 'Solar & EV Infrastructure',
    description:
      'PV system design and installation for commercial properties, including grid-tied systems, battery storage, and EV charging infrastructure. Coordinated with the base electrical scope where applicable.',
  },
];

const Home = () => (
  <div>
    {/* Hero — full-screen video */}
    <section className="relative overflow-hidden min-h-screen blueprint-grid bg-navy-950">
      <video
        src="/videos/jobsite.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 to-navy-950/90 z-10" />
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="relative z-20 flex flex-col justify-end min-h-screen pb-24 max-w-7xl mx-auto px-6"
      >
        <motion.p variants={fadeUp} className="eyebrow">Commercial Electrical Contractor</motion.p>
        <motion.h1
          variants={fadeUp}
          className="font-heading text-display text-white uppercase mt-4 max-w-4xl"
        >
          Commercial Electrical Contractor
        </motion.h1>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          BPR Electric self-performs electrical work on commercial, mixed-use, and residential projects
          across Massachusetts. We coordinate directly with GC superintendents and carry consistent
          manpower from rough-in through final inspection.
        </motion.p>
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 mt-10">
          <Link to="/projects" className="btn-primary inline-flex items-center justify-center">
            Review Our Project Work
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <Link to="/contact" className="btn-ghost inline-flex items-center justify-center">
            Start a Preconstruction Conversation
          </Link>
        </motion.div>
      </motion.div>
    </section>

    {/* Stats */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div key={index} variants={fadeUp} className="text-center">
              <stat.icon className="h-6 w-6 text-primary-500 mx-auto mb-4" />
              <div className="font-heading text-h2 text-white leading-none">{stat.value}</div>
              <div className="text-slate-400 text-sm mt-3">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>

    {/* Featured Projects */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-12">
          <div>
            <motion.p variants={fadeUp} className="eyebrow">Recent Work</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
              Recent Work
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mt-4">
              Commercial and mixed-use projects across Massachusetts.
            </motion.p>
          </div>
          <Link
            to="/projects"
            className="hidden md:inline-flex items-center text-primary-500 hover:text-primary-400 font-heading uppercase text-sm tracking-wide transition-colors"
          >
            View All Projects
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {projects.slice(0, 3).map((project) => (
            <motion.div
              key={project.id}
              variants={fadeUp}
              whileHover={{ x: 4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="group relative overflow-hidden border border-navy-600 border-l-2 border-l-navy-600 hover:border-l-primary-500 transition-colors duration-200"
            >
              <div className="h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-300"
                />
              </div>
              <div className="p-6">
                <div className="text-xs text-slate-400 font-medium uppercase tracking-wide mb-2">
                  {project.location}
                </div>
                <h3 className="font-heading text-white uppercase text-lg mt-1">{project.title}</h3>
                <p className="text-slate-400 text-sm mt-3 line-clamp-2">{project.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 text-center md:hidden">
          <Link to="/projects" className="btn-primary inline-flex items-center">
            View All Projects
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </motion.section>

    {/* What We Do */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">What We Do</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4">
          What We Do
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          BPR Electric handles commercial electrical from preconstruction through closeout — design
          coordination, self-performed installation, and field supervision on projects of all scales.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="border-t border-navy-600 pt-8 pb-8 pr-8"
            >
              <service.icon className="h-6 w-6 text-primary-500 mb-6" />
              <h3 className="font-heading text-h3 text-white uppercase">{service.title}</h3>
              <p className="text-slate-400 mt-4 leading-relaxed text-sm">{service.description}</p>
              <Link
                to="/projects"
                className="inline-flex items-center text-primary-500 hover:text-primary-400 font-heading uppercase text-sm tracking-wide mt-6 transition-colors"
              >
                See Our Work
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>

    {/* CTA */}
    <motion.section
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      className="section-standard bg-navy-950"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.p variants={fadeUp} className="eyebrow">Get Started</motion.p>
        <motion.h2 variants={fadeUp} className="font-heading text-h2 text-white mt-4 max-w-3xl">
          Working on a Commercial Project in Massachusetts?
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mt-6 max-w-2xl">
          We're available for preconstruction conversations. Share the project details and we'll tell
          you directly whether it fits our scope and schedule.
        </motion.p>
        <motion.div variants={fadeUp} className="mt-10">
          <Link to="/contact" className="btn-primary inline-flex items-center">
            Start a Preconstruction Conversation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </motion.section>
  </div>
);

export default Home;
